import { ApolloError, gql, useLazyQuery, useQuery } from '@apollo/client';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { VeevaVaultDocument, VeevaVaultDocumentFilter, VeevaVaultDocumentLimit } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

const GET_ALL_VEEVA_VAULT_DOCUMENTS = gql`
  query getAllVeevaVaultDocument($limit: VeevaVaultDocumentLimit,, $filters: VeevaVaultDocumentFilter) {
    getAllVeevaVaultDocument(limit: $limit, filters: $filters) {
      id,
      name,
      documentNumber,
      status,
      executingTeam,
    }
  }
`;

type ResultType = {
  getAllVeevaVaultDocument: VeevaVaultDocument[];
};

type UseGetVeevaVaultDocument = (limit: VeevaVaultDocumentLimit, filters: VeevaVaultDocumentFilter) => {
  loading: boolean;
  error?: ApolloError;
  data: VeevaVaultDocument[];
};

type UseGetVeevaVaultDocumentLazy = () => {
  getAllVeevaVaultDocumentLazy: Function;
  loading: boolean;
  error?: ApolloError;
  data: VeevaVaultDocument[];
};

// eslint-disable-next-line max-len
const useGetAllVeevaVaultDocument: UseGetVeevaVaultDocument = (limit: VeevaVaultDocumentLimit, filters: VeevaVaultDocumentFilter) => {
  const { data, loading, error } = useQuery<ResultType>(GET_ALL_VEEVA_VAULT_DOCUMENTS, {
    fetchPolicy: 'cache-and-network',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
    variables: {
      limit,
      filters,
    },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getAllVeevaVaultDocument.ts - useGetAllVeevaVaultDocument',
    });
  }

  return {
    data: data?.getAllVeevaVaultDocument || [],
    loading,
    error,
  };
};

export const useGetAllVeevaVaultDocumentLazy: UseGetVeevaVaultDocumentLazy = () => {
  const [getAllVeevaVaultDocumentLazy, { data, loading, error }] = useLazyQuery<ResultType>(GET_ALL_VEEVA_VAULT_DOCUMENTS, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getAllVeevaVaultDocument.ts - useGetAllVeevaVaultDocumentLazy',
    });
  }

  return {
    getAllVeevaVaultDocumentLazy,
    data: data?.getAllVeevaVaultDocument || [],
    loading,
    error,
  };
};

export default useGetAllVeevaVaultDocument;
